import React, { Fragment, memo, useState } from "react";
import Styles from "./ShipmentTable.module.css";
import { AddModal } from "../../../Components/Shipments/ShipmentModal/ShipmentModal";
import { useNavigate } from "react-router-dom";
import ProductsModal from "../../../Components/ProductsModal/ProductsModal";
import { FaTrash, FaEye, FaChevronDown, FaChevronUp } from "react-icons/fa";

const ShipmentTable = (props) => {
  const {
    tableHeaders,
    data,
    productModal,
    setProductModal,
    postData,
    addModal,
    setAddModal,
    deleteStock,
    setRefresh,
    refresh,
    warehouses,
    completed,
  } = props;

  const navigate = useNavigate();
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [items, setItems] = useState();
  const [products, setProducts] = useState();

  const editShipment = (i) => {
    navigate("/shipments/updateshipment", { state: i });
  };

  // ✅ NEW: Handle products from both old and new format
  const handleProducts = (shipment) => {
    let allProducts = [];

    // New format: get products from all containers
    if (shipment.containers && shipment.containers.length > 0) {
      allProducts = shipment.containers.reduce((acc, container) => {
        const containerProducts = container.items.map((item) => ({
          ...item,
          containerInfo: {
            containerNumber: container.containerNumber,
            vessel: container.vessel,
            sealNumber: container.sealNumber,
          },
        }));
        return acc.concat(containerProducts);
      }, []);
    } else {
      // Old format: use items directly
      allProducts = shipment.items || [];
    }

    setProducts(allProducts);
    setProductModal(true);
  };

  // ✅ NEW: Toggle row expansion
  const toggleRowExpansion = (shipmentId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(shipmentId)) {
      newExpandedRows.delete(shipmentId);
    } else {
      newExpandedRows.add(shipmentId);
    }
    setExpandedRows(newExpandedRows);
  };

  // ✅ NEW: Get total items count from shipment
  const getTotalItemsCount = (shipment) => {
    if (shipment.containers && shipment.containers.length > 0) {
      return shipment.containers.reduce((total, container) => {
        return total + (container.items?.length || 0);
      }, 0);
    }
    return shipment.items?.length || 0;
  };

  // ✅ NEW: Get container count
  const getContainerCount = (shipment) => {
    if (shipment.containers && shipment.containers.length > 0) {
      return shipment.containers.length;
    }
    return shipment.containerNumber ? 1 : 0;
  };

  // ✅ NEW: Get primary vessel info
  const getPrimaryVessel = (shipment) => {
    if (shipment.Vessel) return shipment.Vessel;
    if (shipment.containers && shipment.containers.length > 0) {
      return shipment.containers[0].vessel || "Multiple Vessels";
    }
    return "N/A";
  };

  // ✅ NEW: Get primary container info
  const getPrimaryContainer = (shipment) => {
    if (shipment.containerNumber) return shipment.containerNumber;
    if (shipment.containers && shipment.containers.length > 0) {
      return shipment.containers.length > 1
        ? `${shipment.containers[0].containerNumber} (+${
            shipment.containers.length - 1
          } more)`
        : shipment.containers[0].containerNumber;
    }
    return "N/A";
  };

  return (
    <>
      <div className={Styles.tableContainer}>
        {productModal && (
          <ProductsModal
            show={productModal}
            setShow={setProductModal}
            products={products}
            title={"Products in the current Shipment"}
          />
        )}
        {addModal && (
          <AddModal
            show={addModal}
            warehouses={warehouses}
            setShow={setAddModal}
            i={items}
          />
        )}

        <table className={Styles.tableStyle}>
          <thead className={Styles.tableHeader}>
            <tr>
              <th>Expand</th>
              {tableHeaders?.map((header, index) => (
                <th key={index}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data?.map((shipment, index) => (
              <Fragment key={shipment._id}>
                {/* Main Row */}
                <tr className={Styles.mainRow}>
                  <td>
                    <button
                      className={Styles.expandBtn}
                      onClick={() => toggleRowExpansion(shipment._id)}
                      title={`${
                        expandedRows.has(shipment._id) ? "Collapse" : "Expand"
                      } container details`}
                    >
                      {expandedRows.has(shipment._id) ? (
                        <FaChevronUp />
                      ) : (
                        <FaChevronDown />
                      )}
                    </button>
                  </td>
                  <td className={Styles.blNumber}>{shipment?.blNumber}</td>
                  <td className={Styles.vesselCell}>
                    <div className={Styles.vesselInfo}>
                      <span className={Styles.primaryVessel}>
                        {getPrimaryVessel(shipment)}
                      </span>
                    </div>
                  </td>
                  <td>
                    <button
                      className={`popupBtn ${Styles.itemsBtn}`}
                      onClick={() => handleProducts(shipment)}
                      title="View all products in this shipment"
                    >
                      <span className={Styles.itemCount}>
                        {getTotalItemsCount(shipment)}
                      </span>
                      <FaEye className={Styles.eyeIcon} />
                    </button>
                  </td>
                  <td className={Styles.containerCell}>
                    <div className={Styles.containerInfo}>
                      <span className={Styles.primaryContainer}>
                        {getPrimaryContainer(shipment)}
                      </span>
                      {getContainerCount(shipment) > 1 && (
                        <span className={Styles.containerBadge}>
                          {getContainerCount(shipment)} containers
                        </span>
                      )}
                    </div>
                  </td>
                  <td>{shipment?.sealNumber || "Multiple"}</td>
                  <td>{new Date(shipment?.etd).toLocaleDateString("en-GB")}</td>
                  <td>{new Date(shipment?.eta).toLocaleDateString("en-GB")}</td>
                  <td>
                    {new Date(shipment?.unloadDate).toLocaleDateString("en-GB")}
                  </td>
                  <td>
                    <span
                      className={`${Styles.statusBadge} ${
                        Styles[shipment.status]
                      }`}
                    >
                      {shipment.status}
                    </span>
                  </td>
                  {!completed && (
                    <td className={Styles.actionBtns}>
                      <button
                        className="editBtn"
                        onClick={() => {
                          postData(shipment);
                          setAddModal(true);
                          setItems(shipment);
                        }}
                        title="Transfer stock to warehouse"
                      >
                        Transfer
                      </button>
                      <button
                        className="editBtn"
                        onClick={() => editShipment(shipment)}
                        title="Edit shipment details"
                      >
                        Edit
                      </button>
                      <button
                        className="deleteBtn"
                        onClick={() => {
                          window.confirm(
                            "Are you sure you want to delete this Shipment?"
                          ) && deleteStock(shipment._id);
                        }}
                        title="Delete shipment"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  )}
                </tr>

                {/* ✅ NEW: Expanded Container Details */}
                {expandedRows.has(shipment._id) &&
                  shipment.containers &&
                  shipment.containers.length > 0 && (
                    <tr className={Styles.expandedRow}>
                      <td colSpan={completed ? 10 : 11}>
                        <div className={Styles.containerDetails}>
                          <h4 className={Styles.containerTitle}>
                            📦 Container Details ({shipment.containers.length})
                          </h4>
                          <div className={Styles.containerGrid}>
                            {shipment.containers.map(
                              (container, containerIndex) => (
                                <div
                                  key={containerIndex}
                                  className={Styles.containerCard}
                                >
                                  <div className={Styles.containerHeader}>
                                    <h5 className={Styles.containerName}>
                                      🚢 Container {containerIndex + 1}
                                    </h5>
                                  </div>
                                  <div className={Styles.containerBody}>
                                    <div className={Styles.containerInfo}>
                                      <div className={Styles.infoRow}>
                                        <span className={Styles.infoLabel}>
                                          Container #:
                                        </span>
                                        <span className={Styles.infoValue}>
                                          {container.containerNumber}
                                        </span>
                                      </div>
                                      <div className={Styles.infoRow}>
                                        <span className={Styles.infoLabel}>
                                          Vessel:
                                        </span>
                                        <span className={Styles.infoValue}>
                                          {container.vessel}
                                        </span>
                                      </div>
                                      <div className={Styles.infoRow}>
                                        <span className={Styles.infoLabel}>
                                          Seal #:
                                        </span>
                                        <span className={Styles.infoValue}>
                                          {container.sealNumber || "N/A"}
                                        </span>
                                      </div>
                                      <div className={Styles.infoRow}>
                                        <span className={Styles.infoLabel}>
                                          Products:
                                        </span>
                                        <span className={Styles.infoValue}>
                                          {container.items?.length || 0}
                                        </span>
                                      </div>
                                    </div>
                                    {container.items &&
                                      container.items.length > 0 && (
                                        <div className={Styles.productsPreview}>
                                          <h6 className={Styles.productsTitle}>
                                            Products:
                                          </h6>
                                          <div className={Styles.productsList}>
                                            {container.items
                                              .slice(0, 3)
                                              .map((item, itemIndex) => (
                                                <div
                                                  key={itemIndex}
                                                  className={Styles.productItem}
                                                >
                                                  <span
                                                    className={
                                                      Styles.productName
                                                    }
                                                  >
                                                    {item.product
                                                      ?.productName ||
                                                      "Unknown Product"}
                                                  </span>
                                                  <span
                                                    className={
                                                      Styles.productQuantity
                                                    }
                                                  >
                                                    Qty: {item.quantity}
                                                  </span>
                                                </div>
                                              ))}
                                            {container.items.length > 3 && (
                                              <div
                                                className={Styles.moreProducts}
                                              >
                                                +{container.items.length - 3}{" "}
                                                more products
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default memo(ShipmentTable);
